import { useMemo, useState } from 'react'
import { BOVEDILLA_COVERAGE_M2, CATALOG, CatalogItem, Category, CategoryId, findItem } from '../../data/catalog'
import { FREE_DELIVERY_THRESHOLD } from '../../data/site'
import { formatMXN } from '../../utils/currency'
import { openWhatsApp } from '../../utils/whatsapp'
import { trackLeadSubmit, trackWhatsAppClick } from '../../utils/analytics'

export interface OrderLine {
  item: CatalogItem
  category: Category
  qty: number
  total: number
}

const MAX_QTY = 9999

export function useOrderBuilder() {
  const [categoryId, setCategoryId] = useState<CategoryId>('bovedilla')
  const [quantities, setQuantities] = useState<Record<string, number>>({})
  const [m2, setM2] = useState('')
  const [helperItemId, setHelperItemId] = useState('bov-15')
  const [customOpen, setCustomOpen] = useState(false)
  const [customNote, setCustomNote] = useState('')
  const [factura, setFactura] = useState(false)
  const [ciudad, setCiudad] = useState('')
  const [attempted, setAttempted] = useState(false)

  const category = CATALOG.find((c) => c.id === categoryId) ?? CATALOG[0]

  const setQty = (itemId: string, qty: number) =>
    setQuantities((prev) => {
      const next = { ...prev }
      const safe = Number.isFinite(qty) ? Math.floor(qty) : 0
      if (safe <= 0) delete next[itemId]
      else next[itemId] = Math.min(safe, MAX_QTY)
      return next
    })

  const stepQty = (itemId: string, delta: number) => setQty(itemId, (quantities[itemId] ?? 0) + delta)

  const piezasFromM2 = useMemo(() => {
    const value = parseFloat(m2.replace(',', '.'))
    return value > 0 ? Math.ceil(value / BOVEDILLA_COVERAGE_M2) : null
  }, [m2])

  const applyM2 = () => {
    if (piezasFromM2) setQty(helperItemId, piezasFromM2)
  }

  const lines = useMemo<OrderLine[]>(() => {
    const result: OrderLine[] = []
    Object.entries(quantities).forEach(([id, qty]) => {
      const found = findItem(id)
      if (found) result.push({ ...found, qty, total: qty * found.item.price })
    })
    return result
  }, [quantities])

  const subtotal = lines.reduce((sum, line) => sum + line.total, 0)
  const totalPieces = lines.reduce((sum, line) => sum + line.qty, 0)
  const remainingForFree = Math.max(0, FREE_DELIVERY_THRESHOLD - subtotal)
  const freeDelivery = subtotal >= FREE_DELIVERY_THRESHOLD
  const progress = Math.min(1, subtotal / FREE_DELIVERY_THRESHOLD)

  const errors = {
    items: lines.length === 0 ? 'Agrega al menos un material a tu pedido.' : '',
  }
  const hasErrors = Boolean(errors.items)

  const message = useMemo(() => {
    const itemLines = lines
      .map(
        (l) =>
          `• ${l.qty} ${l.category.unit} — ${l.item.name} ${l.item.spec} (${formatMXN(l.item.price)} c/u) = ${formatMXN(l.total)}`,
      )
      .join('\n')
    const delivery = freeDelivery
      ? 'Entrega: SIN COSTO (pedido desde $5,000)'
      : `Entrega: por cotizar (me faltan ${formatMXN(remainingForFree)} para entrega sin costo)`
    return [
      'Hola OA LOSAS, quiero cotizar este pedido:',
      '',
      itemLines,
      '',
      `Total estimado: ${formatMXN(subtotal)} (${totalPieces} piezas)`,
      delivery,
      customOpen && customNote.trim() ? `Medida personalizada (altura y largo): ${customNote.trim()}` : '',
      `Factura: ${factura ? 'Sí' : 'No'}`,
      `Ciudad de entrega: ${ciudad.trim()}`,
      '¿Para cuándo lo necesito?:',
    ]
      .filter((line, i, arr) => line !== '' || arr[i - 1] !== '')
      .join('\n')
  }, [lines, freeDelivery, remainingForFree, subtotal, totalPieces, customOpen, customNote, factura, ciudad])

  const send = () => {
    setAttempted(true)
    if (hasErrors) return
    trackLeadSubmit({ source: 'cotizador', subtotal, piezas: totalPieces, ciudad: ciudad.trim() })
    trackWhatsAppClick('cotizador', 'pedido')
    openWhatsApp(message)
  }

  return {
    categoryId,
    setCategoryId,
    category,
    quantities,
    setQty,
    stepQty,
    m2,
    setM2,
    piezasFromM2,
    helperItemId,
    setHelperItemId,
    applyM2,
    customOpen,
    setCustomOpen,
    customNote,
    setCustomNote,
    factura,
    setFactura,
    ciudad,
    setCiudad,
    lines,
    subtotal,
    totalPieces,
    remainingForFree,
    freeDelivery,
    progress,
    attempted,
    errors,
    send,
  }
}

export type OrderBuilderState = ReturnType<typeof useOrderBuilder>
